/**
 * Recording Controls Component
 *
 * Provides start/stop recording buttons and displays recording duration
 */

import React from 'react';
import { Box, Button, Typography, LinearProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FiberManualRecord, Stop } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { formatDuration, RECOMMENDED_RECORDING_DURATION_MS } from '../../../../../common/recordingUtilities';

const useStyles = makeStyles((theme) => ({
  container: {
    textAlign: 'center',
    color: '#fff'
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2)
  },
  durationText: {
    fontSize: '1.5rem',
    fontWeight: 600,
    fontFamily: 'monospace',
    color: '#fff',
    minWidth: 100
  },
  recordButton: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: theme.palette.error.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.error.dark
    },
    '&.recording': {
      backgroundColor: '#fff',
      color: theme.palette.error.main
    }
  },
  recordingDot: {
    animation: '$blink 1.5s infinite',
    fontSize: '2rem'
  },
  '@keyframes blink': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.3 },
    '100%': { opacity: 1 }
  },
  progressBar: {
    marginTop: theme.spacing(1),
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.3)'
  },
  statusText: {
    color: '#fff',
    fontSize: '0.875rem',
    marginBottom: theme.spacing(0.5),
    textShadow: '0 1px 2px rgba(0,0,0,0.5)'
  }
}));

interface RecordingControlsProps {
  isRecording: boolean;
  recordingDuration: number; // milliseconds
  onStartRecording: () => void;
  onStopRecording: () => void;
  disabled?: boolean;
}

export default function RecordingControls({
  isRecording,
  recordingDuration,
  onStartRecording,
  onStopRecording,
  disabled = false
}: RecordingControlsProps): JSX.Element {
  const classes = useStyles();
  const { t } = useTranslation();

  // Calculate progress (0-100%)
  const progress = Math.min(
    (recordingDuration / RECOMMENDED_RECORDING_DURATION_MS) * 100,
    100
  );

  const formattedDuration = formatDuration(recordingDuration);
  const recommendedDuration = formatDuration(RECOMMENDED_RECORDING_DURATION_MS);

  return (
    <Box className={classes.container}>
      {/* Status Text */}
      {isRecording && (
        <Typography className={classes.statusText}>
          <FiberManualRecord className={classes.recordingDot} style={{ fontSize: '0.75rem', verticalAlign: 'middle', marginRight: 4 }} />
          {t('session.recording', {ns: 'guidedrecording'})}
        </Typography>
      )}

      {/* Main Controls Row */}
      <Box className={classes.controlsRow}>
        {/* Timer Display */}
        <Typography className={classes.durationText}>
          {formattedDuration}
        </Typography>

        {/* Record/Stop Button */}
        <Button
          className={`${classes.recordButton} ${isRecording ? 'recording' : ''}`}
          onClick={isRecording ? onStopRecording : onStartRecording}
          disabled={disabled}
        >
          {isRecording ? <Stop fontSize="large" /> : <FiberManualRecord fontSize="large" />}
        </Button>
      </Box>

      {/* Progress Bar (shown during recording) */}
      {isRecording && (
        <LinearProgress
          variant="determinate"
          value={progress}
          className={classes.progressBar}
          color={progress >= 100 ? 'primary' : 'secondary'}
        />
      )}

      {/* Recommended Duration hint (when not recording) */}
      {!isRecording && (
        <Typography className={classes.statusText} style={{ marginTop: 4 }}>
          {t('session.recommendedduration', {ns: 'guidedrecording'})}
        </Typography>
      )}
    </Box>
  );
}
